import React from 'react';
import { FiBookOpen, FiDatabase, FiInfo, FiX } from 'react-icons/fi';
import { getEddyUiCopy, programmeDisplayName } from './eddyBetaUi';

function formatValue(value) {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.map(formatValue).join(', ');
  if (typeof value === 'object') {
    return Object.entries(value)
      .map(([key, item]) => `${key}: ${formatValue(item)}`)
      .join(' · ');
  }
  return String(value);
}

function DetailRows({ values }) {
  const rows = Object.entries(values || {}).filter(([, value]) => (
    value !== null && value !== undefined && value !== ''
  ));
  if (rows.length === 0) return null;
  return (
    <div className="mt-3 overflow-hidden rounded-2xl border border-edge">
      {rows.map(([label, value]) => (
        <div key={label} className="grid grid-cols-[8rem_1fr] gap-3 border-b border-edge px-4 py-3 last:border-b-0">
          <span className="text-[10px] font-semibold capitalize text-ink-subtle">{label.replaceAll(/([A-Z])/g, ' $1').replaceAll('_', ' ')}</span>
          <span className="text-xs leading-relaxed text-ink-muted">{formatValue(value)}</span>
        </div>
      ))}
    </div>
  );
}

function ProgrammeDetailDrawer({ programme, onClose, onAsk, language = 'en' }) {
  if (!programme) return null;
  const copy = getEddyUiCopy(language);
  const sections = programme.sections || {};
  const overview = sections.overview || programme;
  const name = programmeDisplayName({ ...programme, ...overview }, language);
  const institution = overview.institution || programme.institution || '';
  const overviewFacts = {
    funding: overview.funding,
    duration: overview.duration,
    intake: overview.intake,
    interview: overview.interview
  };
  const admissionReference = sections.admissionReference || null;

  return (
    <div className="fixed inset-0 z-[70] flex justify-end bg-black/35 backdrop-blur-[2px]" role="dialog" aria-modal="true" aria-label={`${name} details`} onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div className="h-full w-full max-w-lg overflow-y-auto border-l border-edge bg-surface shadow-2xl">
        <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-edge bg-surface/95 px-6 py-5 backdrop-blur">
          <div>
            <p className="text-xs font-semibold text-link">{institution}</p>
            <h2 className="mt-1 text-xl font-semibold leading-tight text-ink">{name}</h2>
            <p className="mt-1 text-xs text-ink-subtle">{copy.jupasCode} · {programme.programmeCode}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-full border border-edge p-2.5 text-ink-muted transition hover:bg-surface-muted" aria-label="Close programme details"><FiX aria-hidden /></button>
        </div>

        <div className="space-y-6 px-6 py-6">
          <section className="rounded-2xl border border-link/20 bg-accent/15 p-5">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-link">{copy.programmeOverview}</p>
            {overview.description ? <p className="mt-3 text-xs leading-relaxed text-ink-muted">{overview.description}</p> : null}
            <DetailRows values={overviewFacts} />
          </section>

          {sections.requirements ? <section><h3 className="flex items-center gap-2 text-sm font-semibold text-ink"><FiBookOpen className="text-link" aria-hidden /> {copy.admissionRequirements}</h3><DetailRows values={sections.requirements} /></section> : null}
          {sections.scoreCalculation ? <section className="rounded-2xl border border-edge bg-surface-muted/45 p-4"><h3 className="flex items-center gap-2 text-sm font-semibold text-ink"><FiInfo className="text-link" aria-hidden /> {copy.calculationInformation}</h3><DetailRows values={sections.scoreCalculation} /></section> : null}

          <section className="rounded-2xl border border-edge p-4">
            <h3 className="flex items-center gap-2 text-xs font-semibold text-ink"><FiDatabase className="text-link" aria-hidden /> {copy.storedAdmissionReference}</h3>
            {admissionReference ? <DetailRows values={admissionReference} /> : <p className="mt-2 text-xs leading-relaxed text-ink-muted">{copy.referenceUnavailable}</p>}
            <p className="mt-3 border-t border-edge pt-3 text-[10px] leading-relaxed text-ink-subtle">{copy.referenceBoundary}</p>
          </section>

          <button type="button" onClick={() => onAsk(programme)} className="w-full rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary-hover">{copy.askAboutProgramme}</button>
        </div>
      </div>
    </div>
  );
}

export default ProgrammeDetailDrawer;
